import BaseService from "@/services/BaseService.ts";
import {supabase} from "@supabaseClient";
import logger from "@logger";
import {IndexFundHolding, IndexFundPriceHistory} from "@/integrations/supabase/indexfunds.models.ts";
import {getIsoDateStringFromTimeRange} from "@timeUtils";

/**
 * Index Funds service handles index fund operations
 */
class IndexFundsService extends BaseService {

    /**
     * Fetches The price of an index on the market
     * @param {string} fundSymbol symbol of the index
     */
    async fetchIndexPrice(fundSymbol: string): Promise<IndexFundPrice> {
        logger.info(`Fetching index price for symbol ${fundSymbol}`, {fundSymbol});
        const { data, error } = await supabase.functions.invoke('index-fund-price', {
            body: { symbol: fundSymbol },
        });

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    /**
     * Fetch index fund price history
     * @param {IndexFundPriceHistoryFetchRequest} request request
     */
    async fetchIndexFundPriceHistory(request: IndexFundPriceHistoryFetchRequest): Promise<IndexFundPriceHistory[]> {
        const { fundSymbol, period } = request;
        logger.info(`Fetching index fund price history for symbol ${fundSymbol} and period ${period}`);

        let query = supabase
            .from('index_fund_price_history')
            .select('*')
            .eq('fund_symbol', fundSymbol)
            .order('fetched_at', { ascending: true });

        const cutoffDate = getIsoDateStringFromTimeRange(period);
        if (cutoffDate) {
            query = query.gte('fetched_at', cutoffDate);
        }

        const { data, error } = await query;
        if (error) {
            await this.handleError(error);
            throw error;
        }
        return data
    }

    /**
     * Fetches index fund holdings for a given usr
     * @param {string} userId User ID
     */
    async fetchIndexFundHoldings(userId: string): Promise<IndexFundHolding[]> {
        logger.info(`Fetching index fund holdings for user ${userId}`);
        const { data, error } = await supabase
            .from('index_fund_holdings')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    async createIndexFundHolding(request: IndexFundHoldingCreateRequest): Promise<IndexFundHolding> {
        logger.info("Creating index fund", request);
        const payload = this.convertRequestToSnakeCase(request);

        const { data, error } = await supabase
            .from('index_fund_holdings')
            .insert(payload)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    async updateIndexFundHolding(request: IndexFundHoldingUpdateRequest): Promise<IndexFundHolding> {
        logger.info("Updating index fund", request);
        const payload = this.convertRequestToSnakeCase(request);

        const { data, error } = await supabase
            .from('index_fund_holdings')
            .update(payload)
            .eq('id', payload.id)
            .eq("user_id", payload.user_id)
            .select()
            .single();

        if (error) {
            await this.handleError(error);
            throw error;
        }

        return data
    }

    async deleteIndexFundHolding(request: IndexFundHoldingDeleteRequest): Promise<void> {
        logger.info("Deleting index fund", request);
        const { error } = await supabase
            .from('index_fund_holdings')
            .delete()
            .eq('id', request.id)
            .eq('user_id', request.userId);

        if (error) {
            await this.handleError(error);
            throw error;
        }
    }

}

export default new IndexFundsService();
